import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { badgeOf, formatCost, formatScore, isLabMediaUrl, modelLabel, modelName, modelPath, resultPath, vendorName } from './format'
import { SCENES, type SceneSlug } from './scenes'
import { useT } from './i18n'
import type { LabLeaderRow, LabResult } from './types'

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false)
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!query) return
    setReduce(query.matches)
    const onChange = () => setReduce(query.matches)
    query.addEventListener?.('change', onChange)
    return () => query.removeEventListener?.('change', onChange)
  }, [])
  return reduce
}

/**
 * The drawing itself. Animated results play the SVG through <img>, which never
 * runs script or loads remote content; reduced motion shows the PNG still.
 */
export function PelicanMedia({ result, large = false, eager = false }: { result: Pick<LabResult, 'model' | 'kind' | 'png' | 'svg' | 'ok' | 'error' | 'title' | 'status'>; large?: boolean; eager?: boolean }) {
  const t = useT()
  const reduce = usePrefersReducedMotion()
  const priority = large || eager
  const png = isLabMediaUrl(result.png) ? result.png : undefined
  const svg = isLabMediaUrl(result.svg) ? result.svg : undefined
  const label = `${modelLabel(result.model)}: ${result.title || t.media.pelicanTitle}`

  if (result.kind === 'animated' && svg && !reduce) {
    return <img src={svg} alt={`${label} (${t.media.animation})`} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" />
  }
  if (png) return <img src={png} alt={label} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" />
  if (result.status !== 'done' && result.status !== 'failed') {
    return <div className="media-state"><span className="pulse">{result.status === 'pending' ? t.common.waiting : `${result.status}…`}</span></div>
  }
  return (
    <div className="media-state">
      <span className="fail-mark" aria-hidden="true">✕</span>
      <span>{t.media.noPelican}</span>
      {result.error ? <span className="mono small clamp">{result.error}</span> : null}
    </div>
  )
}

export function Plate({ children }: { children: ReactNode }) {
  return <div className="plate">{children}</div>
}

/**
 * The game itself, playable right on the page. The model-written HTML runs
 * inside a sandboxed iframe: scripts allowed, but no network access and no
 * access to the parent document.
 */
export function GameMedia({ result, large = false, scene = 'endless-runner' }: { result: Pick<LabResult, 'model' | 'html' | 'ok' | 'error' | 'title' | 'status'>; large?: boolean; scene?: SceneSlug }) {
  const t = useT()
  const info = SCENES[scene]
  const html = isLabMediaUrl(result.html) ? result.html : undefined
  const label = `${modelLabel(result.model)}: ${result.title || info.classicTitle.toLowerCase()}`
  if (html) {
    return (
      <iframe
        src={html}
        title={label}
        sandbox="allow-scripts"
        loading={large ? 'eager' : 'lazy'}
        className="game-frame"
        aria-label={`${label} (${t.media.playable(t.scene[scene].unit)})`}
      />
    )
  }
  if (result.status !== 'done' && result.status !== 'failed') {
    return <div className="media-state"><span className="pulse">{result.status === 'pending' ? t.common.waiting : `${result.status}…`}</span></div>
  }
  return (
    <div className="media-state">
      <span className="fail-mark" aria-hidden="true">✕</span>
      <span>{t.media.noUnit(t.scene[scene].unit)}</span>
      {result.error ? <span className="mono small clamp">{result.error}</span> : null}
    </div>
  )
}

/** Captions of the watermelon probe's three frames, in order. */
export const FRAME_CAPTIONS = ['First look', 'After 20 bands', 'The burst']

/**
 * A browser-rendered result on a card: the first screenshot the probe took.
 * The page itself only runs on the result page (see SceneMedia).
 */
export function FramesMedia({ result, eager = false }: { result: Pick<LabResult, 'model' | 'png' | 'frames' | 'ok' | 'error' | 'title' | 'status'>; eager?: boolean }) {
  const t = useT()
  const first = [result.png, result.frames?.[0]].find((u) => isLabMediaUrl(u))
  if (first) return <img src={first} alt={`${modelLabel(result.model)}: ${result.title}, ${t.media.frameCaptions[0].toLowerCase()}`} loading={eager ? 'eager' : 'lazy'} decoding="async" />
  if (result.status !== 'done' && result.status !== 'failed') {
    return <div className="media-state"><span className="pulse">{result.status === 'pending' ? t.common.waiting : `${result.status}…`}</span></div>
  }
  return (
    <div className="media-state">
      <span className="fail-mark" aria-hidden="true">✕</span>
      <span>{t.media.noToy}</span>
      {result.error ? <span className="mono small clamp">{result.error}</span> : null}
    </div>
  )
}

/** The probe's screenshots with their captions (result page). */
export function FrameStrip({ result }: { result: Pick<LabResult, 'model' | 'frames'> }) {
  const t = useT()
  const frames = (result.frames ?? []).filter((u) => isLabMediaUrl(u))
  if (!frames.length) return null
  return (
    <ol className="grid-cards frame-strip" aria-label={t.media.screenshotsAria}>
      {frames.map((src, i) => (
        <li key={src}>
          <Plate><img src={src} alt={`${modelLabel(result.model)}, ${(t.media.frameCaptions[i] ?? t.media.frameFallback(i + 1)).toLowerCase()}`} loading="lazy" decoding="async" /></Plate>
          <p className="eyebrow muted">{i + 1} · {t.media.frameCaptions[i] ?? t.media.frameFallback(i + 1)}</p>
        </li>
      ))}
    </ol>
  )
}

/** Picks the right media viewer for the scene. */
export function SceneMedia({ result, scene, large, eager }: { result: LabResult; scene: SceneSlug; large?: boolean; eager?: boolean }) {
  const media = SCENES[scene].media
  if (media === 'game') return <GameMedia result={result} large={large} scene={scene} />
  // Browser-rendered toys play on the result page; cards show the first frame.
  if (media === 'frames') return large && isLabMediaUrl(result.html) ? <GameMedia result={result} large scene={scene} /> : <FramesMedia result={result} eager={eager} />
  return <PelicanMedia result={result} large={large} eager={eager} />
}

/**
 * A landing page's hero. A result with a screenshot shows it the way cards do
 * (SceneMedia); an html result without one gets a click-to-play cover, then
 * the result page's sandboxed GameMedia (/lab/media/…html redirects to the
 * sandbox origin). The game only loads after the click, so the first screen
 * never runs model-written code on its own. No result: the `empty` message.
 */
export function HeroMedia({ result, scene, empty }: { result?: LabResult; scene: SceneSlug; empty: string }) {
  const t = useT()
  const [playing, setPlaying] = useState(false)
  const game = useRef<HTMLDivElement>(null)
  // The cover button disappears on click: hand focus to the game so the keyboard keeps working.
  useEffect(() => { if (playing) game.current?.querySelector('iframe')?.focus() }, [playing])
  if (!result) return <div className="scene-cover"><span className="eyebrow">{empty}</span></div>
  const label = modelLabel(result.model)
  const still = [result.png, result.frames?.[0]].some((u) => isLabMediaUrl(u))
  const sticker = <span className="sticker">{label}</span>
  if (still || !isLabMediaUrl(result.html)) return <><div className="scene-cover"><SceneMedia result={result} scene={scene} eager /></div>{sticker}</>
  if (playing) return <div ref={game} className="hero-game"><GameMedia result={result} large scene={scene} /></div>
  const score = result.score != null ? `${formatScore(result.score)}/5` : null
  return (
    <>
      <button
        type="button"
        className="hero-play"
        onClick={() => setPlaying(true)}
        aria-label={t.media.clickToPlayAria(label, t.scene[scene].unit, result.score)}
      >
        <span className="hero-play-icon" aria-hidden="true">▶</span>
        <span className="eyebrow">{t.media.clickToPlay}</span>
        <span className="small">{score ? t.media.topScore(score) : t.media.topPick}</span>
      </button>
      {sticker}
    </>
  )
}

export function ScoreMeter({ value }: { value: number }) {
  const on = Math.round(value)
  return <span className="meter" aria-hidden="true">{[1, 2, 3, 4, 5].map((n) => <i key={n} data-on={n <= on ? '' : undefined} />)}</span>
}

export function ResultCard({ result, showAuthor = true, eager = false, scene = 'pelican-test' }: { result: LabResult; showAuthor?: boolean; eager?: boolean; scene?: SceneSlug }) {
  const t = useT()
  const badge = badgeOf(result)
  const finished = result.status === 'done' || result.status === 'failed'
  const unit = t.scene[scene].unit
  const body = (
    <>
      <div className="card-media">
        <Plate><SceneMedia result={result} scene={scene} eager={eager} /></Plate>
        {badge && finished ? <span className="badge" data-tone={badge.tone}>{t.common.badge(badge.label)}</span> : null}
        <span className="kind-tag">{t.common.kind(result.kind)}</span>
      </div>
      <div className="card-body">
        <span className="eyebrow accent">{result.classic ? t.scene[scene].classic : result.title}</span>
        <span className="card-title">{modelLabel(result.model)}</span>
        {showAuthor ? <span className="mono small muted">{t.media.by(result.author)}</span> : null}
        <span className="card-foot">
          <span><b className="num">{formatScore(result.score)}</b><span className="mono small muted"> / 5</span></span>
          <span><b className="num">{formatCost(result.cost)}</b><span className="mono small muted"> {t.media.perUnit(unit)}</span></span>
        </span>
      </div>
    </>
  )
  const label = t.media.cardAria(modelLabel(result.model), t.common.kind(result.kind), result.score)
  return finished
    ? <Link to={resultPath(result.id, scene)} className="card" aria-label={t.media.openDetails(label)}>{body}</Link>
    : <div className="card" aria-label={label}>{body}</div>
}

export function ResultGrid({ items, showAuthor = true, empty, scene = 'pelican-test' }: { items: LabResult[]; showAuthor?: boolean; empty?: string; scene?: SceneSlug }) {
  const t = useT()
  if (!items.length) return <p className="muted">{empty ?? t.media.nothingYet}</p>
  return <div className="grid-cards">{items.map((r, index) => <ResultCard key={r.id} result={r} showAuthor={showAuthor} eager={index < 8} scene={scene} />)}</div>
}

export function Leaderboard({ rows, justTested, scene = 'pelican-test' }: { rows: LabLeaderRow[]; justTested: Set<string>; scene?: SceneSlug }) {
  const t = useT()
  const info = SCENES[scene]
  const unitPlural = t.scene[scene].units
  if (!rows.length) return <p className="muted center pad">{t.media.noUnitsYet(unitPlural)}</p>
  return (
    <div className="table-wrap" role="region" aria-label={t.media.leaderboardAria(SCENES[scene].name)} tabIndex={0}>
      <table className="board">
        <thead><tr>
          <th scope="col">{t.media.rank}</th><th scope="col">{t.media.model}</th><th scope="col">{t.media.overall}</th>
          <th scope="col" className="r">{t.scene[scene].ok}</th>
          {info.tracksMotion ? <th scope="col" className="r">{t.media.actuallyMoves}</th> : null}
          <th scope="col" className="r">{t.media.avgCost}</th><th scope="col" className="r">{unitPlural[0].toUpperCase() + unitPlural.slice(1)}</th>
        </tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.model}>
              <td><span className="rank" data-podium={row.rank <= 3 ? row.rank : undefined}>{row.rank}</span></td>
              <td>
                <Link to={modelPath(row.model, scene)} className="model-cell">
                  <span aria-hidden="true" className="avatar">{modelName(row.model).charAt(0)}</span>
                  <span>
                    <span className="strong">{modelLabel(row.model)}</span>
                    {justTested.has(row.model) ? <span className="pill">{t.media.newPill}</span> : null}
                    <span className="mono small muted block">{vendorName(row.model) || row.model}{row.sota ? <span className="sota-badge">SOTA</span> : null} · {t.common.publishedAt(row.published_at)} · {t.common.testers(row.testers)}</span>
                  </span>
                </Link>
              </td>
              <td className="num">{row.score != null ? <><ScoreMeter value={row.score} /> {row.score.toFixed(2)}</> : '—'}</td>
              <td className="r num">{Math.round(row.ok_rate * 100)}%</td>
              {info.tracksMotion ? <td className="r num">{row.motion_rate == null ? '—' : `${Math.round(row.motion_rate * 100)}%`}</td> : null}
              <td className="r num">{formatCost(row.avg_cost)}</td>
              <td className="r num">{row.results}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const t = useT()
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked */ }
  }
  return <button type="button" className="link-btn" onClick={copy} aria-live="polite">{copied ? t.common.copied : (label ?? t.common.copy)}</button>
}

export function ShareButtons({ path, text }: { path: string; text: string }) {
  const t = useT()
  const url = `${window.location.origin}${path}`
  const x = `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
  return (
    <div className="share">
      <a className="btn btn-ink" href={x} target="_blank" rel="noopener noreferrer">{t.common.shareOnX}</a>
      <CopyButton text={url} label={t.common.copyLink} />
    </div>
  )
}

export function SectionHead({ id, eyebrow, title, lede, action }: { id?: string; eyebrow: string; title: string; lede?: string; action?: ReactNode }) {
  return (
    <div className="section-head">
      <div>
        <p className="eyebrow muted">{eyebrow}</p>
        <h2 id={id}>{title}</h2>
        {lede ? <p className="lede muted">{lede}</p> : null}
      </div>
      {action}
    </div>
  )
}

export function Loading({ label }: { label?: string }) {
  const t = useT()
  return <div className="measure pad-xl mono muted" aria-live="polite">{label ?? t.common.loading}</div>
}
